import { useEffect, useRef, useState } from "react";
import { Camera, Circle, CircleDot, ImageIcon, Moon, Video, X } from "lucide-react";
import {
  disableChatWebPush,
  enableChatWebPush,
  getChatWebPushStatus,
  isChatWebPushSupported,
  type ChatWebPushStatus,
} from "@/lib/chatWebPush";
import {
  fetchChatProfile,
  setChatPresenceStatus,
  updateChatProfile,
  type ChatPresenceStatus,
  type ChatProfile,
} from "@/lib/chatApi";
import {
  persistChatNotificationSoundMuted,
  readChatNotificationSoundMuted,
} from "@/lib/chatNotificationSoundPrefs";
import { CHAT_INSTALL_URL, chatInstallQrDataUrl } from "@/lib/chatInstallQr";
import { resolveChatAvatarUrl } from "@/lib/chatAvatar";

type Props = {
  suiteUserId: string;
  username: string;
  status: ChatPresenceStatus;
  onStatusChange: (status: ChatPresenceStatus) => void;
  onClose: () => void;
  onUpdated: (profile: ChatProfile) => void;
};

const STATUS_OPTIONS: Array<{
  id: Exclude<ChatPresenceStatus, "offline">;
  label: string;
  hint: string;
  Icon: typeof Circle;
  dotClass: string;
}> = [
  {
    id: "online",
    label: "En ligne",
    hint: "Visible comme disponible",
    Icon: CircleDot,
    dotClass: "text-[var(--chat-online)]",
  },
  {
    id: "dnd",
    label: "Ne pas déranger",
    hint: "Pastille rouge pour les collègues",
    Icon: Moon,
    dotClass: "text-[var(--chat-dnd)]",
  },
  {
    id: "meeting",
    label: "En réunion",
    hint: "Pastille ambre pour les collègues",
    Icon: Video,
    dotClass: "text-[var(--chat-meeting)]",
  },
];

/** Mobile / tablette tactile : bouton appareil photo pertinent. Desktop → fichier. */
function useChatCameraPreferred(): boolean {
  const [ok, setOk] = useState(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia("(pointer: coarse)").matches ||
      window.matchMedia("(max-width: 767px)").matches
    );
  });
  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse), (max-width: 767px)");
    const sync = () => setOk(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return ok;
}

export function ChatProfileModal({
  suiteUserId,
  username,
  status,
  onStatusChange,
  onClose,
  onUpdated,
}: Props) {
  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickOpen, setPickOpen] = useState(false);
  const [soundMuted, setSoundMuted] = useState(() => readChatNotificationSoundMuted());
  const [pushStatus, setPushStatus] = useState<ChatWebPushStatus>(() =>
    isChatWebPushSupported() ? "default" : "unsupported"
  );
  const [pushBusy, setPushBusy] = useState(false);
  const [pushHint, setPushHint] = useState<string | null>(null);
  const [qrSrc] = useState(() => chatInstallQrDataUrl());
  const cameraPreferred = useChatCameraPreferred();
  const cameraRef = useRef<HTMLInputElement>(null);
  const libraryRef = useRef<HTMLInputElement>(null);

  const effectiveStatus: Exclude<ChatPresenceStatus, "offline"> =
    status === "offline" ? "online" : status;

  useEffect(() => {
    let cancelled = false;
    void fetchChatProfile(suiteUserId).then((r) => {
      if (cancelled) return;
      setLoading(false);
      if (r.success && r.data) {
        setDisplayName(r.data.display_name ?? "");
        setAvatarUrl(resolveChatAvatarUrl(r.data.avatar_url, suiteUserId));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [suiteUserId]);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  useEffect(() => {
    setSoundMuted(readChatNotificationSoundMuted());
  }, []);

  useEffect(() => {
    let cancelled = false;
    void getChatWebPushStatus(suiteUserId).then((s) => {
      if (!cancelled) setPushStatus(s);
    });
    return () => {
      cancelled = true;
    };
  }, [suiteUserId]);

  const shown = preview || avatarUrl;
  const initial = (displayName || username).slice(0, 1).toUpperCase();

  const applyFile = (f: File | undefined) => {
    if (!f) return;
    setPendingFile(f);
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
    setPickOpen(false);
  };

  const onAvatarClick = () => {
    if (cameraPreferred) {
      setPickOpen((v) => !v);
      return;
    }
    libraryRef.current?.click();
  };

  const pickStatus = async (next: Exclude<ChatPresenceStatus, "offline">) => {
    if (statusBusy || next === effectiveStatus) return;
    setStatusBusy(true);
    const prev = status;
    onStatusChange(next);
    const r = await setChatPresenceStatus(suiteUserId, next);
    setStatusBusy(false);
    if (!r.success) {
      onStatusChange(prev);
      return;
    }
    onStatusChange(r.data?.presence ?? next);
  };

  const pushOn = pushStatus === "subscribed";

  const toggleMute = () => {
    const next = !soundMuted;
    setSoundMuted(next);
    persistChatNotificationSoundMuted(next);
  };

  const togglePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    setPushHint(null);
    try {
      if (pushOn) {
        const r = await disableChatWebPush(suiteUserId);
        setPushStatus(r.status);
      } else {
        const r = await enableChatWebPush(suiteUserId);
        setPushStatus(r.status);
        if (!r.ok && r.message) setPushHint(r.message);
      }
    } finally {
      setPushBusy(false);
    }
  };

  const pushLabel = (() => {
    if (pushBusy) return "Mise à jour…";
    if (pushHint) return pushHint;
    if (pushStatus === "subscribed") return "Alertes OS hors premier plan";
    if (pushStatus === "denied") return "Bloquées par le navigateur";
    if (pushStatus === "unsupported") return "Non supportées sur cet appareil";
    if (pushStatus === "not_configured") return "Serveur non configuré (VAPID)";
    if (pushStatus === "granted_off" || pushStatus === "default") return "Désactivées";
    return "Indisponibles";
  })();

  const save = async () => {
    setSaving(true);
    setError(null);
    const r = await updateChatProfile(suiteUserId, {
      display_name: displayName.trim(),
      avatar: pendingFile ?? undefined,
    });
    setSaving(false);
    if (r.success && r.data) {
      onUpdated(r.data);
      onClose();
    } else {
      setError(r.message || "Enregistrement impossible.");
    }
  };

  return (
    <div className="chat-app-sheet-overlay" onClick={onClose} role="presentation">
      <div
        className="chat-panel chat-app-sheet"
        role="dialog"
        aria-labelledby="chat-profile-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="chat-app-sheet__handle" aria-hidden />
        <header className="flex shrink-0 items-center justify-between border-b border-[var(--chat-border)] px-4 py-3">
          <h3 id="chat-profile-title" className="font-semibold">
            Mon profil
          </h3>
          <button type="button" onClick={onClose} aria-label="Fermer">
            <X className="size-4" />
          </button>
        </header>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {loading ? (
            <p className="text-sm text-[var(--chat-muted)]">Chargement…</p>
          ) : (
            <>
              <div className="flex flex-col items-center gap-2">
                <button
                  type="button"
                  className="relative size-24 overflow-hidden rounded-full bg-[var(--chat-accent-soft)] text-2xl font-semibold text-[var(--chat-accent-strong)]"
                  onClick={onAvatarClick}
                  title="Changer la photo"
                  aria-expanded={cameraPreferred ? pickOpen : undefined}
                >
                  {shown ? (
                    <img src={shown} alt="" className="size-full object-cover" />
                  ) : (
                    initial
                  )}
                  <span className="absolute inset-x-0 bottom-0 flex justify-center bg-black/45 py-1">
                    <Camera className="size-4 text-white" />
                  </span>
                </button>

                {cameraPreferred ? (
                  <input
                    ref={cameraRef}
                    type="file"
                    accept="image/*"
                    capture="user"
                    className="hidden"
                    onChange={(e) => {
                      applyFile(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                ) : null}
                <input
                  ref={libraryRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    applyFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />

                {cameraPreferred && pickOpen ? (
                  <div className="flex w-full flex-col gap-1.5 rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] p-1.5">
                    <button
                      type="button"
                      className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[var(--chat-text)] transition hover:bg-[var(--chat-surface-elevated)] active:scale-[0.99]"
                      onClick={() => cameraRef.current?.click()}
                    >
                      <Camera className="size-4 shrink-0 text-[var(--chat-accent-strong)]" />
                      Prendre une photo
                    </button>
                    <button
                      type="button"
                      className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[var(--chat-text)] transition hover:bg-[var(--chat-surface-elevated)] active:scale-[0.99]"
                      onClick={() => libraryRef.current?.click()}
                    >
                      <ImageIcon className="size-4 shrink-0 text-[var(--chat-accent-strong)]" />
                      Choisir dans la galerie
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-[var(--chat-muted)]">
                    {cameraPreferred
                      ? "Appuyez pour changer la photo"
                      : "Cliquez pour choisir une image"}
                  </p>
                )}
              </div>

              <label className="block space-y-1">
                <span className="text-xs text-[var(--chat-muted)]">Nom affiché</span>
                <input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={username}
                  className="w-full rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 py-2 text-sm outline-none focus:border-[var(--chat-accent)]"
                />
              </label>

              <section className="space-y-1.5">
                <p className="text-xs font-medium uppercase tracking-wide text-[var(--chat-muted)]">
                  Statut
                </p>
                <div className="overflow-hidden rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] p-1">
                  {STATUS_OPTIONS.map((opt) => {
                    const active = effectiveStatus === opt.id;
                    const Icon = opt.Icon;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        disabled={statusBusy}
                        onClick={() => void pickStatus(opt.id)}
                        className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition ${
                          active
                            ? "bg-[var(--chat-accent-soft)]"
                            : "hover:bg-[var(--chat-surface-elevated)]"
                        } disabled:opacity-60`}
                      >
                        <Icon className={`size-4 shrink-0 ${opt.dotClass}`} aria-hidden />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-[var(--chat-text)]">
                            {opt.label}
                          </span>
                          <span className="block text-[10px] text-[var(--chat-muted)]">
                            {opt.hint}
                          </span>
                        </span>
                        {active ? (
                          <Circle className="size-2.5 fill-[var(--chat-accent-strong)] text-[var(--chat-accent-strong)]" />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </section>

              <section className="rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 py-2.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[var(--chat-text)]">
                      Son des notifications
                    </p>
                    <p className="text-[10px] text-[var(--chat-muted)]">
                      {soundMuted ? "Coupé" : "Activé"} · choix du son dans Thème
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!soundMuted}
                    title={soundMuted ? "Réactiver le son" : "Couper le son"}
                    onClick={toggleMute}
                    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                      soundMuted ? "bg-[var(--chat-border)]" : "bg-[var(--chat-accent)]"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 size-5 rounded-full bg-white shadow transition-transform ${
                        soundMuted ? "left-0.5" : "left-[1.35rem]"
                      }`}
                    />
                  </button>
                </div>
                <div className="mt-2.5 flex items-center justify-between gap-3 border-t border-[var(--chat-border)] pt-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[var(--chat-text)]">
                      Notifications système
                    </p>
                    <p className="text-[10px] leading-snug text-[var(--chat-muted)]">
                      {pushLabel}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={pushOn}
                    aria-label="Notifications système"
                    disabled={
                      pushBusy ||
                      pushStatus === "unsupported" ||
                      pushStatus === "denied" ||
                      pushStatus === "not_configured"
                    }
                    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                      pushOn ? "bg-[var(--chat-accent)]" : "bg-[var(--chat-border)]"
                    }`}
                    onClick={() => void togglePush()}
                  >
                    <span
                      className={`absolute top-0.5 size-5 rounded-full bg-white shadow transition-transform ${
                        pushOn ? "left-[1.35rem]" : "left-0.5"
                      }`}
                    />
                  </button>
                </div>
              </section>

              <section className="rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 py-3">
                <p className="text-sm font-medium text-[var(--chat-text)]">Installer sur mobile</p>
                <p className="mt-0.5 text-[10px] leading-snug text-[var(--chat-muted)]">
                  Scannez pour ouvrir Chat et l’ajouter à l’écran d’accueil
                </p>
                <div className="mt-2.5 flex justify-center rounded-lg bg-white p-2">
                  <img
                    src={qrSrc}
                    alt={`QR code ${CHAT_INSTALL_URL}`}
                    width={148}
                    height={148}
                    className="size-36"
                  />
                </div>
                <a
                  href={CHAT_INSTALL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 block truncate text-center text-[10px] text-[var(--chat-accent-strong)] hover:underline"
                >
                  {CHAT_INSTALL_URL}
                </a>
              </section>

              {error ? <p className="text-xs text-[var(--chat-danger)]">{error}</p> : null}
              <button
                type="button"
                disabled={saving}
                className="w-full rounded-xl bg-[var(--chat-accent)] py-2.5 text-sm font-medium text-[#0f172a] disabled:opacity-50"
                onClick={() => void save()}
              >
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
